import { Link } from "react-router-dom";
import {
  EnvelopeSimple,
  TwitterLogo,
  LinkedinLogo,
  GithubLogo,
} from "@phosphor-icons/react";
import { OPERO_BRAND, OPERO_LOGOS } from "@opero/ui/brand";
import { useFeature } from "../context/FeatureFlagContext";

const footerLinks = {
  Product: [
    { label: "Features", href: "#features" },
    { label: "Workflow", href: "#workflow", flag: "WORKFLOW_AUTOMATION" as const },
    { label: "Pricing", href: "#pricing" },
  ],
  Company: [
    { label: "About", href: "/about", isRoute: true },
    { label: "Blog", href: "/blog", isRoute: true, flag: "BLOG" as const },
    { label: "Press", href: "/press", isRoute: true },
    { label: "Contact", href: "/contact", isRoute: true },
  ],
  Resources: [
    { label: "Documentation", href: "/docs", isRoute: true },
    // { label: "API Reference", href: "/api", isRoute: true },
    { label: "Community", href: "/community", isRoute: true },
    { label: "Status Page", href: "/status", isRoute: true },
    { label: "Security", href: "/security", isRoute: true },
  ],
  Legal: [
    { label: "Privacy Policy", href: "/privacy", isRoute: true },
    { label: "Terms of Service", href: "/terms", isRoute: true },
    { label: "DPA", href: "/dpa", isRoute: true },
    { label: "Cookie Policy", href: "/cookies", isRoute: true },
  ],
};

export function Footer() {
  const blogEnabled = useFeature("BLOG");
  const workflowEnabled = useFeature("WORKFLOW_AUTOMATION");
  const flagState = { BLOG: blogEnabled, WORKFLOW_AUTOMATION: workflowEnabled };
  return (
    <footer className="bg-slate-900 dark:bg-slate-950 border-t border-slate-800">
      <div className="mx-auto max-w-7xl px-6 py-16">
        <div className="grid gap-12 lg:grid-cols-6">
          {/* Brand */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-primary-500 to-primary-400 shadow-lg shadow-primary-500/25">
                <img src={OPERO_LOGOS.mark} alt="Opero" className="h-5 w-5" />
              </div>
              <span className="text-xl font-bold tracking-tight text-white">
                Opero
              </span>
            </div>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-400">
              Task management for teams that ship fast. Stop managing work
              about work.
            </p>
            <div className="mt-6 flex gap-3">
              {[
                { icon: TwitterLogo, label: "Twitter" },
                { icon: LinkedinLogo, label: "LinkedIn" },
                { icon: GithubLogo, label: "GitHub" },
                { icon: EnvelopeSimple, label: "Email" },
              ].map(({ icon: Icon, label }) => (
                <a
                  key={label}
                  href="#"
                  aria-label={label}
                  className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-800 text-slate-500 transition-all duration-200 hover:border-primary-500/50 hover:text-primary-400"
                >
                  <Icon size={18} weight="bold" />
                </a>
              ))}
            </div>
          </div>

          {/* Link columns */}
          {Object.entries(footerLinks).map(([heading, links]) => (
            <div key={heading}>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                {heading}
              </h3>
              <ul className="mt-4 space-y-3">
                {links
                  .filter((link) =>
                    "flag" in link && link.flag ? flagState[link.flag] : true,
                  )
                  .map((link) => (
                  <li key={link.label}>
                    {"isRoute" in link && link.isRoute ? (
                      <Link
                        to={link.href}
                        className="text-sm text-slate-500 transition-colors duration-200 hover:text-primary-400"
                      >
                        {link.label}
                      </Link>
                    ) : (
                      <a
                        href={link.href}
                        className="text-sm text-slate-500 transition-colors duration-200 hover:text-primary-400"
                      >
                        {link.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-col items-center justify-between gap-4 border-t border-slate-800 pt-8 sm:flex-row">
          <p className="text-xs text-slate-500">{OPERO_BRAND.copyright()}</p>
          <p className="text-xs text-slate-600">
            Built for teams that refuse to settle.
          </p>
        </div>
      </div>
    </footer>
  );
}
